import { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios'; // Importa AxiosInstance
import { getFormUrl } from '@tools/conns/PathHandler';
// import { logInfo } from '@tools/LogsHandler.tsx';

type QueryPrimitive = string | number | boolean | bigint | Date | null | undefined;

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const cloneSearchParams = (params: URLSearchParams): URLSearchParams => {
  const clone = new URLSearchParams();
  params.forEach((value, key) => {
    clone.append(key, value);
  });
  return clone;
};

const normalizePrimitiveValue = (value: QueryPrimitive): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return value.toISOString();
  }
  switch (typeof value) {
    case 'string':
      return value.trim() || null;
    case 'number':
      return Number.isFinite(value) ? String(value) : null;
    case 'boolean':
      return value ? 'true' : 'false';
    case 'bigint':
      return value.toString();
    default:
      return null;
  }
};

const appendParamValue = (params: URLSearchParams, key: string, value: unknown): void => {
  if (value === undefined || value === null) {
    return;
  }
  if (value instanceof URLSearchParams) {
    value.forEach((entryValue, entryKey) => {
      params.append(entryKey, entryValue);
    });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => appendParamValue(params, key, entry));
    return;
  }
  if (value instanceof Set) {
    value.forEach((entry) => appendParamValue(params, key, entry));
    return;
  }
  if (value instanceof Date) {
    const normalized = normalizePrimitiveValue(value);
    if (normalized) {
      params.append(key, normalized);
    }
    return;
  }
  if (typeof value === 'object') {
    try {
      const serialized = JSON.stringify(value);
      if (serialized && serialized !== '{}') {
        params.append(key, serialized);
      }
    } catch {
      const fallback = (value as { toString?: () => string }).toString?.();
      if (isNonEmptyString(fallback)) {
        params.append(key, fallback.trim());
      }
    }
    return;
  }
  const normalized = normalizePrimitiveValue(value as QueryPrimitive);
  if (normalized) {
    params.append(key, normalized);
  }
};

const buildSearchParams = (input: unknown): URLSearchParams | undefined => {
  if (!input) {
    return undefined;
  }
  if (input instanceof URLSearchParams) {
    return cloneSearchParams(input);
  }
  if (typeof input !== 'object') {
    return undefined;
  }
  const entries = Object.entries(input as Record<string, unknown>);
  if (!entries.length) {
    return undefined;
  }
  const params = new URLSearchParams();
  entries.forEach(([key, value]) => appendParamValue(params, key, value));
  return params;
};

const mergeSearchParams = (
  primary?: URLSearchParams,
  secondary?: URLSearchParams,
): URLSearchParams | undefined => {
  if (!primary && !secondary) {
    return undefined;
  }
  if (!secondary) {
    return primary ? cloneSearchParams(primary) : undefined;
  }
  if (!primary) {
    return cloneSearchParams(secondary);
  }
  const merged = cloneSearchParams(secondary);
  primary.forEach((value, key) => {
    merged.append(key, value);
  });
  return merged;
};

const hasSearchParamsEntries = (params: URLSearchParams | undefined): params is URLSearchParams => {
  if (!params) {
    return false;
  }
  const iterator = params.keys();
  return !iterator.next().done;
};

// Función para obtener el token CSRF desde las cookies
const getCsrfToken = (): string | undefined => {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='))
    ?.split('=')[1];
};

const buildHeaders = (csrfToken: string | undefined, _data?: unknown) => {
  return {
    Accept: 'application/json',
    'X-CSRFToken': csrfToken ?? '',
  } as Record<string, string>;
};

// Función dedicada para generar la URL con argumentos
export const generateUrlWithArgs = (
  urlName: string,
  args: Array<number | string | boolean> = []
): string => {
  // 1. Obtengo la URL base
  let url = getFormUrl(urlName);

  // 2. Me aseguro de que termine en slash
  if (!url.endsWith('/')) {
    url += '/';
  }   

  // 3. Si hay argumentos, los convierto a strings, los codifico y los uno con slash
  if (args.length > 0) {
    const segments = args
      .map(String)                // convierte número/booleano a string
      .map(encodeURIComponent)    // escapa caracteres especiales
      .join('/');
    url += `${segments}/`;
  }

  return url;
};

// Nueva función factoría que recibe una instancia de Axios
export function createApiHelpers(axiosInstance: AxiosInstance) {
  // Objeto que encapsula las llamadas HTTP con manejo de CSRF
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const conns = <T = any,>(
    url_name: string,
    args: (number | string | boolean)[] = []
  ) => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      const urlWithArgs = generateUrlWithArgs(url_name, args);
      const { headers: extraHeaders, params: extraParams, ...rest } = config;
      const headers = {
        ...buildHeaders(csrfToken),
        ...(extraHeaders ?? {}),
      } as Record<string, string>;
      const payloadParams = buildSearchParams(data);
      const configParams = buildSearchParams(extraParams);
      const mergedParams = mergeSearchParams(payloadParams, configParams);
      const requestConfig: AxiosRequestConfig = {
        ...rest,
        headers,
      };
      if (hasSearchParamsEntries(mergedParams)) {
        requestConfig.params = mergedParams;
      }
      return await axiosInstance.get(urlWithArgs, requestConfig);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      const urlWithArgs = generateUrlWithArgs(url_name, args);
      // logInfo('conns - urlWithArgs: ', urlWithArgs);
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.post(urlWithArgs, data, {
        ...config,
        headers: { ...buildHeaders(csrfToken, data), ...(config.headers || {}) },
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    put: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.put(generateUrlWithArgs(url_name, args), data, {
        ...config,
        headers: { ...buildHeaders(csrfToken, data), ...(config.headers || {}) },
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    patch: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.patch(generateUrlWithArgs(url_name, args), data, {
        ...config,
        headers: { ...buildHeaders(csrfToken, data), ...(config.headers || {}) },
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.delete(generateUrlWithArgs(url_name, args), {
        ...config,
        headers: { ...buildHeaders(csrfToken, data), ...(config.headers || {}) },
        data: data,
      });
    },
  });

  // Objeto que encapsula las llamadas HTTP con manejo de CSRF
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const connsDirect = <T = any,>(url: string) => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      // Usar la instancia de Axios proporcionada
      const { headers: extraHeaders, params: extraParams, ...rest } = config;
      const payloadParams = buildSearchParams(data);
      const configParams = buildSearchParams(extraParams);
      const mergedParams = mergeSearchParams(payloadParams, configParams);
      const requestConfig: AxiosRequestConfig = {
        ...rest,
        headers: { ...buildHeaders(undefined), ...(extraHeaders || {}) },
      };
      if (hasSearchParamsEntries(mergedParams)) {
        requestConfig.params = mergedParams;
      }
      return await axiosInstance.get(url, requestConfig);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    post: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.post(url, data, {
        ...config,
        headers: { ...buildHeaders(csrfToken, data), ...(config.headers || {}) },
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    put: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.put(url, data, {
        ...config,
        headers: { ...buildHeaders(csrfToken, data), ...(config.headers || {}) },
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    patch: async (data: any = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.patch(url, data, {
        ...config,
        headers: { ...buildHeaders(csrfToken, data), ...(config.headers || {}) },
      });
    },
    delete: async (config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
      const csrfToken = getCsrfToken();
      // Usar la instancia de Axios proporcionada
      return await axiosInstance.delete(url, {
        ...config,
        headers: { ...buildHeaders(csrfToken), ...(config.headers || {}) },
      });
    },
  });

  return { conns, connsDirect };
}